import clsx from 'clsx';
import React from 'react';
import { useTranslation, Trans } from 'react-i18next';

import Checkbox, { CheckboxProps } from '@material-ui/core/Checkbox';
import FormControl, { FormControlProps } from '@material-ui/core/FormControl';
import FormControlLabel, { FormControlLabelProps } from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormHelperText, { FormHelperTextProps } from '@material-ui/core/FormHelperText';
import Link from '@material-ui/core/Link';

import { useStyles } from './styles';

export type UserConsentInputRenderProps = {
	wrapper: Omit<FormControlProps, 'error'>;
	label: Omit<FormControlLabelProps, 'control' | 'label'>;
	checkbox: Omit<CheckboxProps, 'checked' | 'onChange'>;
	helperText: FormHelperTextProps;
};

export type ComponentProps = {
	checked: boolean;
	inputError: boolean;
	renderProps?: Partial<UserConsentInputRenderProps>;
	onConsentChange(value: boolean): void;
};

const UserConsentInput = (props: ComponentProps) => {
	const { checked, inputError, renderProps } = props;
	const { onConsentChange } = props;

	const classes = useStyles();
	const { t } = useTranslation();

	const handleChangeConsent: React.ChangeEventHandler<HTMLInputElement> = React.useCallback(
		ev => {
			onConsentChange(ev.target.checked);
		},
		[onConsentChange]
	);

	return (
		<FormControl {...renderProps?.wrapper} error={inputError}>
			<FormGroup row>
				<FormControlLabel
					control={
						<Checkbox
							id={`userConsentCheckbox`}
							checked={checked}
							onChange={handleChangeConsent}
							name="userConsent"
							color="primary"
							size="small"
							{...renderProps?.checkbox}
						/>
					}
					label={
						<Trans i18nKey="userConsent.checkboxLabel" t={t}>
							I consent to the collection, use and processing of my personal data as described in the
							<Link target="_blank" href="https://www.launchmetrics.com/galleries-privacy-notice" rel="noreferrer">
								Privacy Notice
							</Link>
							.
						</Trans>
					}
					{...renderProps?.label}
					classes={{
						root: clsx(classes.rootLabel, renderProps?.label?.classes?.root),
						label: clsx(classes.label, renderProps?.label?.classes?.label)
					}}
				/>
			</FormGroup>
			{inputError && (
				<FormHelperText {...renderProps?.helperText} className={clsx(classes.helperText, renderProps?.helperText?.className)}>
					{t('userConsent.consentRequired')}
				</FormHelperText>
			)}
		</FormControl>
	);
};

export default React.memo(UserConsentInput);
